import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import Accordion from '@/components/Accordion/Accordion';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import List from '@/components/List/List';
import Quote from '@/components/Quote/Quote';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';

const TextSearchSandboxContainer = () => {
  const color = useColor();
  const [input, setInput] = useState('');
  const setNewModal = useDetailModalStore((state) => state.setNewModal);

  const fetchFeedbackList = async (pageParam: null | string) => {
    const PAGE_SIZE = 50;
    const data = await service.globalSearch.fetchTextSearchSandbox({
      query: input,
      cursor: pageParam,
    });

    return {
      list: data || [],
      cursor: (data && data?.[PAGE_SIZE]?.paginate_token) || null,
    };
  };

  const searchQuery = useInfiniteQuery({
    queryKey: ['text-search-sandbox', input],
    queryFn: async ({ pageParam }) => fetchFeedbackList(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage.cursor;
    },
    enabled: false,
  });
  return (
    <Style g={16}>
      <Heading type="h1">Text Search Sandbox</Heading>
      <Flex g={8} width="600px">
        <Input
          sizeType="l"
          style={{ backgroundColor: color.gray100 }}
          value={input}
          placeholder="Search text"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              searchQuery.refetch();
            }
          }}
        />
        <Button
          sizeType="l"
          variant="primary"
          onClick={() => {
            searchQuery.refetch();
          }}
        >
          Search
        </Button>
      </Flex>
      <VFlex className="rule-info-container">
        <Accordion
          anchor={(isOpen) => (
            <Flex
              j="space-between"
              f={1}
              style={{ cursor: 'pointer', padding: 16, fontSize: 16 }}
            >
              <Flex a="center" g={8}>
                <InfoCircleOutlined />
                <Typo type="label1" weight="m">
                  Rule info
                </Typo>
              </Flex>
              <DownOutlined
                style={{
                  rotate: isOpen ? '-180deg' : '0deg',
                  transition: 'all 0.2s',
                }}
              />
            </Flex>
          )}
          content={
            <VFlex g={16} style={{ padding: 16 }}>
              <Quote icon="info" title="Rule">
                <Typo type="body2" className="text-gray-800">
                  사용자가 원하는 단어를 word라 명한다
                  <br />
                  word의 조합(단일 word / 복수 word 가능)을 chunk라 명한다
                  chunk는 "" 큰
                  <br />
                  따옴표로 묶어서 표현한다. 검색 기본 단위는 chunk 이다. chunk와
                  chunk
                  <br />
                  사이에는 and / or 오퍼레이션을 넣을 수 있다. 다수의
                  오퍼레이션이
                  <br />
                  필요할 경우 단일 종류만 선택 가능하다
                </Typo>
              </Quote>
              <Quote icon="info" title="예제">
                <Typo type="body2" className="text-gray-800">
                  "주차장 전기차" and "시설" and "이용"
                  <br />
                  "주차장" or "전기차" or "시설"
                </Typo>
              </Quote>
            </VFlex>
          }
        />
      </VFlex>

      <List
        className="flex-1 gap-2 pb-16"
        data={searchQuery.data?.pages.flatMap((page) => page.list)}
        isLoading={searchQuery.isLoading}
        hasNextPage={searchQuery.hasNextPage}
        fetchNextPage={searchQuery.fetchNextPage}
        isFetchingNextPage={searchQuery.isFetchingNextPage}
        render={(item) => {
          return (
            <VFlex
              key={item.alias}
              css={css`
                padding: 16px 24px;
                background-color: ${(props) => props.theme.colors.gray100};
                gap: 8px;
                border-radius: 8px;
                border: 1px solid ${(props) => props.theme.colors.gray400};
                cursor: pointer;
                transition: all 0.2s;
                &:hover {
                  background-color: ${(props) => props.theme.colors.gray200};
                }
              `}
              onClick={() => {
                setNewModal({
                  alias: item.alias,
                  type: DETAIL_MODAL_TYPE.FEEDBACK,
                  idx: 0,
                });
              }}
            >
              <Flex g={8} a="center">
                <TicketLabel type="feedback">{item.tid}</TicketLabel>
                <Typo type="label1">{item.title}</Typo>
              </Flex>
              <Typo type="body2">{item.highlight}</Typo>
            </VFlex>
          );
        }}
      />
    </Style>
  );
};

const Style = styled(VFlex)`
  height: 100vh;
  padding: 32px;
  padding-bottom: 0px;
  background-color: ${({ theme }) => theme.colors.gray200};
  .rule-info-container {
    background-color: ${({ theme }) => theme.colors.gray100};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.gray400};
  }
`;

export default TextSearchSandboxContainer;
